import { z } from 'zod';
import {
  DecimalSchema,
  IdSchema,
  NonNegativeDecimalSchema,
  PaginationQuerySchema,
  PositiveDecimalSchema,
  VersionSchema,
  paginatedSchema,
} from './common';
import { FiscalDocumentTypeSchema, IvaRateSchema } from './fiscal';

export const DOCUMENT_KINDS = ['RECEIPT', 'ISSUE', 'TRANSFER', 'WRITE_OFF', 'STOCKTAKE'] as const;
export const DocumentKindSchema = z.enum(DOCUMENT_KINDS);
export type DocumentKind = z.infer<typeof DocumentKindSchema>;

export const DOCUMENT_STATUSES = ['DRAFT', 'POSTED', 'CANCELLED'] as const;
export const DocumentStatusSchema = z.enum(DOCUMENT_STATUSES);
export type DocumentStatus = z.infer<typeof DocumentStatusSchema>;

/**
 * Behaviour table driving both validation and the posting engine.
 * `effect`:
 *   in   — increases stock at `warehouseId`
 *   out  — decreases stock at `warehouseId`
 *   move — decreases at `warehouseId`, increases at `destinationWarehouseId`
 *   set  — reconciles stock at `warehouseId` to the counted quantity
 */
export const DOCUMENT_KIND_META: Record<
  DocumentKind,
  {
    effect: 'in' | 'out' | 'move' | 'set';
    requiresDestination: boolean;
    requiresCounterparty: boolean;
    /**
     * Last-resort number prefix, used only when a tenant has no numbering
     * setting for the kind. Registration seeds the Spanish prefix, so these
     * defaults are the same Argentine abbreviations.
     */
    prefix: string;
    /** Whether `factQuantity` is meaningful for its lines. */
    usesFactQuantity: boolean;
  }
> = {
  RECEIPT: {
    effect: 'in',
    requiresDestination: false,
    requiresCounterparty: false,
    prefix: 'RE',
    usesFactQuantity: false,
  },
  ISSUE: {
    effect: 'out',
    requiresDestination: false,
    requiresCounterparty: false,
    prefix: 'RS',
    usesFactQuantity: false,
  },
  TRANSFER: {
    effect: 'move',
    requiresDestination: true,
    requiresCounterparty: false,
    prefix: 'TR',
    usesFactQuantity: false,
  },
  WRITE_OFF: {
    effect: 'out',
    requiresDestination: false,
    requiresCounterparty: false,
    prefix: 'BJ',
    usesFactQuantity: false,
  },
  STOCKTAKE: {
    effect: 'set',
    requiresDestination: false,
    requiresCounterparty: false,
    prefix: 'IN',
    usesFactQuantity: true,
  },
};

/**
 * One tax charged on one document line.
 *
 * Argentina and Chile put exactly one row here, because IVA is one tax. Brazil
 * puts several: ICMS, IPI, PIS and COFINS on the same line, each with its own
 * base. The client renders whatever it is given rather than assuming a single
 * rate, which is what lets a document from any country be displayed by the
 * same screen.
 */
export const DocumentLineTaxSchema = z.object({
  /** The country's code for the tax: `IVA`, `ICMS`, `IPI`, `PIS`, `COFINS`. */
  taxCode: z.string(),
  /** What the tax was charged on, in document currency. */
  base: z.string(),
  /** Percent, as the country states it. */
  rate: IvaRateSchema,
  /** The tax itself, in document currency. */
  amount: z.string(),
  /** The authority's code for this rate, where it publishes one. */
  externalCode: z.string().nullable(),
  /** True when the tax was already inside the price rather than added to it. */
  inclusive: z.boolean(),
});
export type DocumentLineTax = z.infer<typeof DocumentLineTaxSchema>;

export const DocumentLineSchema = z.object({
  id: IdSchema,
  productId: IdSchema,
  productSku: z.string(),
  productName: z.string(),
  unitShortName: z.string(),
  /** Document (planned) quantity. */
  quantity: z.string(),
  /** Physically counted quantity — only for STOCKTAKE. */
  factQuantity: z.string().nullable(),
  /** Signed difference `factQuantity - quantity` — only for STOCKTAKE. */
  variance: z.string().nullable().optional(),
  price: z.string(),
  /**
   * The rate of the tax this country treats as its own — IVA in Argentina and
   * Chile, ICMS in Brazil.
   *
   * A summary of one row of `taxes`, kept because every screen and export
   * reads a single rate per line. A country that charges several taxes on one
   * line can only be read from `taxes`.
   */
  ivaRate: IvaRateSchema,
  /** Every tax charged on the line, in the country's own vocabulary. */
  taxes: z.array(DocumentLineTaxSchema),
  /** quantity * price. */
  amount: z.string(),
  /** Stock on hand when the document was created (UI hint). */
  stockOnHand: z.string().nullable().optional(),
  note: z.string().nullable(),
});
export type DocumentLine = z.infer<typeof DocumentLineSchema>;

export const DocumentSchema = z.object({
  id: IdSchema,
  number: z.string(),
  kind: DocumentKindSchema,
  status: DocumentStatusSchema,
  date: z.string(),
  /**
   * Optimistic-concurrency counter.
   *
   * The editor sends it back on save; a write that arrives with a stale one is
   * refused with a 409 rather than silently overwriting whatever the other
   * person wrote.
   */
  version: VersionSchema,
  warehouseId: IdSchema,
  warehouseName: z.string(),
  destinationWarehouseId: IdSchema.nullable(),
  destinationWarehouseName: z.string().nullable().optional(),
  counterpartyId: IdSchema.nullable(),
  counterpartyName: z.string().nullable().optional(),
  /**
   * The class of fiscal document this is, as the country's own code — the
   * pack's `resolveStamp` result: `FACTURA`, `NOTA_DE_CREDITO` in Argentina, a
   * DTE type in Chile. `null` for a document that is not a fiscal event at all
   * (an internal transfer, a stock count).
   *
   * There is no separate `fiscalLetter` any more: it was an Argentine concept
   * the pack stopped returning, and ARCA's numeric type code — which is what
   * actually encodes the letter — is now kept beside this one.
   */
  fiscalDocumentType: FiscalDocumentTypeSchema.nullable(),
  /**
   * The authority's own numeric code for that document type: ARCA's `CbteTipo`
   * (`6` for a Factura B), a Chilean DTE code. `null` when the country has no
   * such code or the document is not fiscal.
   *
   * Kept rather than recomputed, so a filing exports what the document was
   * posted with even if the pack's tables change later.
   */
  fiscalExternalCode: z.number().int().nullable(),
  /** Punto de venta and correlative number, when the tenant uses fiscal numbering. */
  pointOfSale: z.number().int().nullable(),
  /** CAE returned by ARCA, when the document was authorised electronically. */
  cae: z.string().nullable(),
  caeExpiresAt: z.string().nullable(),
  comment: z.string().nullable(),
  totalAmount: z.string(),
  totalIva: z.string(),
  lineCount: z.number().int().nonnegative(),
  postedAt: z.string().nullable(),
  postedByName: z.string().nullable().optional(),
  createdByName: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lines: z.array(DocumentLineSchema).optional(),
});
export type Document = z.infer<typeof DocumentSchema>;

export const DocumentListSchema = paginatedSchema(DocumentSchema);

const DocumentBaseSchema = z.object({
  kind: DocumentKindSchema.describe(
    'Ingreso (RECEIPT), egreso (ISSUE), transferencia (TRANSFER), baja (WRITE_OFF) ' +
      'o inventario (STOCKTAKE).',
  ),
  date: z.coerce.date().default(() => new Date()),
  warehouseId: IdSchema.describe('Depósito de origen, o el único afectado.'),
  destinationWarehouseId: IdSchema.nullable()
    .optional()
    .describe('Solo en una transferencia: el depósito de destino.'),
  counterpartyId: IdSchema.nullable()
    .optional()
    .describe(
      'Proveedor o cliente. Sin contraparte el documento no es fiscal: no ' +
        'lleva letra ni comprobante.',
    ),
  /**
   * Which class of document to record, when the country distinguishes them.
   * Left out, the server asks the pack: a document that brings goods in or
   * sends them out against a counterparty gets the pack's default type, and a
   * document with no counterparty is not a fiscal document at all.
   */
  fiscalDocumentType: FiscalDocumentTypeSchema.nullable()
    .optional()
    .describe(
      'Tipo de comprobante segun el pais. Si se omite, el servidor registra el ' +
        'tipo por defecto cuando el documento tiene contraparte.',
    ),
  comment: z.string().trim().max(1000).nullable().optional(),
  /** Post the document immediately instead of saving it as a draft. */
  post: z.boolean().default(false).describe('Contabilizar ahora en vez de guardar un borrador.'),
  lines: z
    .array(
      z.object({
        productId: IdSchema,
        quantity: PositiveDecimalSchema,
        factQuantity: NonNegativeDecimalSchema.nullable().optional(),
        price: NonNegativeDecimalSchema.default('0').describe('Precio unitario, neto de IVA.'),
        ivaRate: IvaRateSchema.optional().describe(
          'Si se omite, se toma la alícuota del artículo.',
        ),
        note: z.string().trim().max(500).nullable().optional(),
      }),
    )
    .min(1)
    .max(1000),
});

export const CreateDocumentSchema = DocumentBaseSchema.superRefine((value, ctx) => {
  const meta = DOCUMENT_KIND_META[value.kind];

  if (meta.requiresDestination && !value.destinationWarehouseId) {
    ctx.addIssue({
      code: 'custom',
      path: ['destinationWarehouseId'],
      message: 'validation.destinationRequired',
    });
  }
  if (value.destinationWarehouseId && value.destinationWarehouseId === value.warehouseId) {
    ctx.addIssue({
      code: 'custom',
      path: ['destinationWarehouseId'],
      message: 'validation.destinationSameAsSource',
    });
  }

  const seen = new Set<string>();
  value.lines.forEach((line, index) => {
    if (seen.has(line.productId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['lines', index, 'productId'],
        message: 'validation.duplicateProduct',
      });
    }
    seen.add(line.productId);

    if (meta.usesFactQuantity && line.factQuantity == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['lines', index, 'factQuantity'],
        message: 'validation.factQuantityRequired',
      });
    }
  });
});
export type CreateDocument = z.infer<typeof DocumentBaseSchema>;

/**
 * Editing a draft.
 *
 * `version` is how two people editing the same draft are kept apart. omitting
 * it is allowed and means "overwrite whatever is there" — the behaviour of
 * every client written before the field existed, kept so that an older PWA
 * does not start failing on a deploy. The editor always sends it.
 */
export const UpdateDocumentSchema = DocumentBaseSchema.omit({
  kind: true,
  post: true,
})
  .partial()
  .extend({ version: VersionSchema.optional() });
export type UpdateDocument = z.infer<typeof UpdateDocumentSchema>;

/**
 * One editing session on a document.
 *
 * A warehouse is a shared terminal, not a shared document: two storekeepers
 * on the same draft is a mistake waiting to happen, and the API can say so
 * before anyone types anything. The row is a lease with a short life, so a
 * browser that is closed or a tablet that loses power stops holding it
 * without any cleanup.
 */
export const DocumentEditSessionSchema = z.object({
  documentId: IdSchema,
  /** The other people holding the document, excluding the caller. */
  editors: z.array(
    z.object({
      userId: IdSchema,
      fullName: z.string(),
    }),
  ),
  /** When the lease lapses unless it is renewed. */
  expiresAt: z.string(),
});
export type DocumentEditSession = z.infer<typeof DocumentEditSessionSchema>;

export const PostDocumentSchema = z.object({
  /** Optional manual override of the generated document number. */
  number: z.string().trim().min(1).max(64).optional(),
});
export type PostDocument = z.infer<typeof PostDocumentSchema>;

export const DocumentQuerySchema = PaginationQuerySchema.extend({
  kind: DocumentKindSchema.optional(),
  status: DocumentStatusSchema.optional(),
  warehouseId: IdSchema.optional(),
  counterpartyId: IdSchema.optional(),
  dateFrom: z.coerce.date().optional(),
  dateTo: z.coerce.date().optional(),
});
export type DocumentQuery = z.infer<typeof DocumentQuerySchema>;
export type DocumentQueryInput = z.input<typeof DocumentQuerySchema>;

export const DocumentSummarySchema = z.object({
  drafts: z.number().int().nonnegative(),
  postedToday: z.number().int().nonnegative(),
  byKind: z.array(
    z.object({
      kind: DocumentKindSchema,
      count: z.number().int().nonnegative(),
      amount: z.string(),
    }),
  ),
});
export type DocumentSummary = z.infer<typeof DocumentSummarySchema>;

/** Payload preview shown before posting, so users see what will change. */
export const DocumentPreviewSchema = z.object({
  documentId: IdSchema,
  canPost: z.boolean(),
  problems: z.array(
    z.object({
      code: z.string(),
      message: z.string(),
      lineIndex: z.number().int().nonnegative().optional(),
      productId: IdSchema.optional(),
    }),
  ),
  effects: z.array(
    z.object({
      productId: IdSchema,
      productName: z.string(),
      warehouseId: IdSchema,
      warehouseName: z.string(),
      quantityBefore: DecimalSchema,
      quantityAfter: DecimalSchema,
      costBefore: DecimalSchema,
      costAfter: DecimalSchema,
    }),
  ),
});
export type DocumentPreview = z.infer<typeof DocumentPreviewSchema>;
